import { Check, Copy } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";
import { formatClabe } from "@/shared/utils/clabe";
import { parseDateInput } from "@/shared/utils/dateTimeHelpers";

import { PaymentSteps } from "./PaymentSteps";
import type { PaymentInstrumentProps } from "./RailDepositDrawer";
import { WaitingForPayment } from "./WaitingForPayment";

/**
 * A SPEI collection ready to pay: the CLABE lent to THIS charge, the exact amount to send to it, the day it
 * expires and the steps a payer follows at any Mexican bank. The CLABE is shown only here: the runtime leaves
 * it out of `GET /payins/:id`, so it cannot be shown again once the panel closes.
 */
export function SpeiPaymentInstrument({ result, amount, rawAmount }: PaymentInstrumentProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const { copiedValue, copy } = useCopyToClipboard();
  const clabe = result.clabe ?? "";
  // The provider sends a calendar date ("2026-08-24"): read as UTC midnight it would be the day before here.
  const expiresOn = result.expires_at
    ? parseDateInput(result.expires_at).toLocaleDateString(activeLanguage, { dateStyle: "medium" })
    : null;

  return (
    <div className="space-y-4">
      <WaitingForPayment
        label={t("deposit_spei_waiting")}
        trailing={expiresOn ? `${t("deposit_expires_label")} ${expiresOn}` : null}
      />
      {clabe && (
        <section className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="bg-primary/5 px-5 pt-5 pb-4">
            <h4 className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("deposit_spei_clabe_label")}
            </h4>
            <p className="mt-2 font-mono text-2xl font-bold tracking-wider text-foreground tabular-nums sm:text-[1.7rem]">
              {formatClabe(clabe)}
            </p>
            <ColocaButton
              variant={copiedValue === clabe ? "secondary" : "primary"}
              icon={copiedValue === clabe ? Check : Copy}
              onClick={() => void copy(clabe, t("deposit_clabe_copied"))}
              className="mt-3 w-full justify-center sm:w-auto"
            >
              {copiedValue === clabe ? t("deposit_clabe_copied") : t("deposit_clabe_copy")}
            </ColocaButton>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-dashed border-border px-5 py-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{t("deposit_spei_exact_amount")}</p>
              <p className="font-mono text-base font-semibold text-foreground tabular-nums">{amount}</p>
            </div>
            <ColocaIconButton
              icon={copiedValue === rawAmount ? Check : Copy}
              variant="ghost"
              size="sm"
              aria-label={t("deposit_spei_copy_amount")}
              onClick={() => void copy(rawAmount, t("deposit_spei_amount_copied"))}
            />
          </div>
        </section>
      )}
      <PaymentSteps
        title={t("deposit_how_to_pay")}
        steps={[t("deposit_spei_step_1"), t("deposit_spei_step_2", { amount }), t("deposit_spei_step_3")]}
      />
    </div>
  );
}
